import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Chip,
  Pagination,
  Button,
  CircularProgress,
  TableSortLabel,
  Grid,
  Tooltip,
  Alert,
  SelectChangeEvent,
  Card,
  CardContent,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import StorageIcon from "@mui/icons-material/Storage";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import TerminalIcon from "@mui/icons-material/Terminal";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { IBackupTarefaV2 } from "./types";
import { TarefasApi } from "./services/tarefasApi";
import { useLogin } from "../../contexts/LoginContext";
import { useCliente } from "../../contexts/ClienteContext";
import { formatarDataHoraCurta } from "../../common/utils/formatadorDataHora";
import PremiumCard from "../ui/PremiumCard";
import { apiServer } from "../../api";
import { IGrupoTarefa } from "../../types/tarefa";
import { ModalInstrucoesLinux } from "./ModalInstrucoesLinux";

const verificarSeSucesso = (status: string | undefined): boolean => {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === "sucesso" || s === "concluido" || s === "concluído" || s === "executado" || s === "executada";
};

interface IListaTarefasProps {
  onVisualizar: (tarefa: IBackupTarefaV2) => void;
  onEditar: (tarefa: IBackupTarefaV2) => void;
  onCriar: () => void;
}

export const ListaTarefas: React.FC<IListaTarefasProps> = ({
  onVisualizar,
  onEditar,
  onCriar
}) => {
  const [login] = useLogin();
  const [cliente] = useCliente();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Estados dos filtros e paginação
  const [pesquisa, setPesquisa] = useState("");
  const [tipo, setTipo] = useState("Todos");
  const [status, setStatus] = useState("Todos");
  const [grupo, setGrupo] = useState("Todos");
  const [pagina, setPagina] = useState(1);
  const [ordenarPor, setOrdenarPor] = useState("nome");
  const [direcao, setDirecao] = useState<"asc" | "desc">("asc");

  // Dados da API
  const [tarefas, setTarefas] = useState<IBackupTarefaV2[]>([]);
  const [total, setTotal] = useState(0);
  const [listaGrupos, setListaGrupos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [tarefaParaExecutar, setTarefaParaExecutar] = useState<IBackupTarefaV2 | null>(null);
  const [executandoRemoto, setExecutandoRemoto] = useState(false);
  const [mensagemSucesso, setMensagemSucesso] = useState<string | null>(null);
  const [modalLinuxAberto, setModalLinuxAberto] = useState(false);

  const limit = 10;

  // Carregar grupos únicos apenas do cliente logado para o filtro
  useEffect(() => {
    const activeClienteId = cliente.id || login.usuario?.usuarioCliente?.clienteID;
    if (!activeClienteId || !login.accessToken) return;

    apiServer<IGrupoTarefa[]>()
      .get(`/grupo-tarefa-agendada/${activeClienteId}`, login.accessToken)
      .then((res) => {
        if (res.data) {
          const nomes = res.data.map((g) => g.nome).filter(Boolean);
          setListaGrupos(Array.from(new Set(nomes)));
        }
      })
      .catch((err) => {
        console.error("Erro ao carregar grupos do cliente", err);
      });
  }, [login.accessToken, cliente.id, login.usuario?.usuarioCliente?.clienteID]);

  // Carregar tarefas
  const carregarDados = useCallback(async () => {
    const activeClienteId = cliente.id || login.usuario?.usuarioCliente?.clienteID;
    if (!activeClienteId || !login.accessToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const offset = (pagina - 1) * limit;
      const res = await TarefasApi.listarTarefas(activeClienteId, login.accessToken, {
        q: pesquisa || undefined,
        tipo: tipo !== "Todos" ? tipo : undefined,
        status: status !== "Todos" ? status : undefined,
        grupo: grupo !== "Todos" ? grupo : undefined,
        limit,
        offset,
        ordenarPor,
        direcao
      });
      setTarefas(res.items);
      setTotal(res.total);
    } catch (err: any) {
      console.error("Erro ao listar tarefas", err);
      setErro("Não foi possível carregar as tarefas agendadas.");
    } finally {
      setLoading(false);
    }
  }, [cliente.id, login.usuario?.usuarioCliente?.clienteID, login.accessToken, pesquisa, tipo, status, grupo, pagina, ordenarPor, direcao]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const handlePesquisaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPesquisa(e.target.value);
    setPagina(1);
  };

  const handleTipoChange = (e: SelectChangeEvent<string>) => {
    setTipo(e.target.value);
    setPagina(1);
  };

  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    setStatus(e.target.value);
    setPagina(1);
  };

  const handleGrupoChange = (e: SelectChangeEvent<string>) => {
    setGrupo(e.target.value);
    setPagina(1);
  };

  const handleSort = (propriedade: string) => {
    const isAsc = ordenarPor === propriedade && direcao === "asc";
    setDirecao(isAsc ? "desc" : "asc");
    setOrdenarPor(propriedade);
    setPagina(1);
  };

  const handleAlternarStatus = async (tarefa: IBackupTarefaV2, checked: boolean) => {
    if (!login.accessToken) return;
    const novoStatus = checked ? "ativa" : "pausada";
    try {
      await TarefasApi.alterarStatus(tarefa.tarefaAgendadaID, novoStatus, login.accessToken);
      setTarefas((prev) =>
        prev.map((t) =>
          t.tarefaAgendadaID === tarefa.tarefaAgendadaID ? { ...t, status: novoStatus } : t
        )
      );
    } catch (err) {
      console.error("Erro ao alterar status da tarefa", err);
      alert("Falha ao alterar o status da tarefa.");
    }
  };

  const handleConfirmarExecucao = async () => {
    if (!tarefaParaExecutar || !login.accessToken) return;
    setExecutandoRemoto(true);
    try {
      const res = await TarefasApi.executarTarefaRemota(tarefaParaExecutar.tarefaAgendadaID, login.accessToken);
      setMensagemSucesso(res.mensagem || "Ordem de execução manual enviada com sucesso para o agente!");
      setTarefaParaExecutar(null);
    } catch (err: any) {
      setErro(err?.response?.data?.mensagem || "Erro ao solicitar execução da tarefa.");
      setTarefaParaExecutar(null);
    } finally {
      setExecutandoRemoto(false);
    }
  };

  const renderIconeTipo = (tipoTarefa: string) => {
    switch (tipoTarefa) {
      case "SQLServer":
      case "Postgres":
      case "Sybase":
        return <StorageIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />;
      case "CheckDB":
        return <FactCheckIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />;
      case "Arquivos":
        return <InsertDriveFileIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />;
      case "Bat":
        return <TerminalIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />;
      default:
        return null;
    }
  };

  const renderBadgeStatus = (statusTarefa: string) => {
    switch (statusTarefa) {
      case "ativa":
        return <Chip label="Ativa" color="success" size="small" variant="outlined" />;
      case "pausada":
        return <Chip label="Pausada" color="default" size="small" variant="outlined" />;
      case "erro_configuracao":
        return <Chip label="Erro de Configuração" color="warning" size="small" variant="outlined" />;
      case "inativa":
        return <Chip label="Inativa" color="error" size="small" variant="outlined" />;
      default:
        return <Chip label={statusTarefa} color="default" size="small" variant="outlined" />;
    }
  };

  const totalPaginas = Math.ceil(total / limit);

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Tarefas Agendadas
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<TerminalIcon />}
            onClick={() => setModalLinuxAberto(true)}
            sx={{ borderRadius: "8px" }}
          >
            Instalar Agente Linux
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onCriar}
            sx={{ borderRadius: "8px" }}
          >
            Nova Tarefa
          </Button>
        </Box>
      </Box>

      {/* Painel de Filtros */}
      <PremiumCard sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nome..."
              value={pesquisa}
              onChange={handlePesquisaChange}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2.6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={tipo} label="Tipo" onChange={handleTipoChange}>
                <MenuItem value="Todos">Todos os tipos</MenuItem>
                <MenuItem value="SQLServer">SQL Server</MenuItem>
                <MenuItem value="Postgres">Postgres</MenuItem>
                <MenuItem value="Sybase">Sybase</MenuItem>
                <MenuItem value="Arquivos">Arquivos</MenuItem>
                <MenuItem value="Bat">Script BAT</MenuItem>
                <MenuItem value="CheckDB">Diagnóstico de BD</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2.6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={handleStatusChange}>
                <MenuItem value="Todos">Todos os status</MenuItem>
                <MenuItem value="ativa">Ativa</MenuItem>
                <MenuItem value="pausada">Pausada</MenuItem>
                <MenuItem value="erro_configuracao">Erro de Configuração</MenuItem>
                <MenuItem value="inativa">Inativa</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Grupo</InputLabel>
              <Select value={grupo} label="Grupo" onChange={handleGrupoChange}>
                <MenuItem value="Todos">Todos os grupos</MenuItem>
                {listaGrupos.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </PremiumCard>

      {erro && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {erro}
        </Alert>
      )}

      {/* Tabela de Tarefas / Cards Mobile */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : tarefas.length === 0 ? (
        <Paper sx={{ p: 4, borderRadius: "12px", textAlign: "center", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
          <Typography variant="body1" color="text.secondary">
            Nenhuma tarefa encontrada com os filtros selecionados.
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={2} sx={{ p: 0.5 }}>
          {tarefas.map((t) => (
            <Card 
              key={t.tarefaAgendadaID} 
              variant="outlined" 
              sx={{ 
                borderRadius: "12px", 
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid',
                borderColor: 'divider',
                background: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(8px)'
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Header do Card */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {renderIconeTipo(t.tipo)}
                      {t.nome}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.5, flexWrap: 'wrap' }}>
                      {t.plataforma === "linux" ? (
                        <Chip
                          size="small"
                          label="🐧 Linux"
                          color="info"
                          variant="outlined"
                          sx={{ height: 18, fontSize: "10px", fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="🪟 Windows"
                          variant="outlined"
                          sx={{ height: 18, fontSize: "10px", color: "text.secondary" }}
                        />
                      )}
                      {t.grupoNome && (
                        <Typography variant="caption" color="text.secondary">
                          Grupo: {t.grupoNome}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    {renderBadgeStatus(t.status)}
                    <Typography variant="caption" color="text.secondary">
                      {t.tipo}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Grid de Informações */}
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Agendamento
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t.tipoAgendamento} às {t.horaExecucao}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Última Execução
                    </Typography>
                    {t.ultimaExecucao ? (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatarDataHoraCurta(t.ultimaExecucao.dataHora)}
                        </Typography>
                        <Chip
                          label={verificarSeSucesso(t.ultimaExecucao.status) ? "Sucesso" : "Falha"}
                          color={verificarSeSucesso(t.ultimaExecucao.status) ? "success" : "error"}
                          size="small"
                          sx={{ height: 16, fontSize: "10px", mt: 0.5 }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Nunca executou
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Próxima Execução
                    </Typography>
                    {t.proximaExecucao ? (
                      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                        {formatarDataHoraCurta(t.proximaExecucao.dataHora)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Não agendada
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                {/* Ações do Card */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1, color: "text.secondary" }}>
                      Ativa:
                    </Typography>
                    <Switch
                      checked={t.status === "ativa"}
                      onChange={(e) => handleAlternarStatus(t, e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Tooltip title="Executar Agora (Remoto)">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setTarefaParaExecutar(t)}
                        sx={{ mr: 1, backgroundColor: "rgba(25, 118, 210, 0.08)", "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.2)" } }}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver Detalhes">
                      <IconButton size="small" onClick={() => onVisualizar(t)} sx={{ mr: 1 }}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => onEditar(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: "12px", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)", overflowX: "auto" }}>
          <Table aria-label="tabela de tarefas agendadas" sx={{ minWidth: 900 }}>
            <TableHead sx={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={ordenarPor === "nome"}
                    direction={ordenarPor === "nome" ? direcao : "asc"}
                    onClick={() => handleSort("nome")}
                    sx={{ fontWeight: 700 }}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenarPor === "tipo"}
                    direction={ordenarPor === "tipo" ? direcao : "asc"}
                    onClick={() => handleSort("tipo")}
                    sx={{ fontWeight: 700 }}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Agendamento
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Última Execução
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Próxima Execução
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Status
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Ativa
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tarefas.map((t) => (
                <TableRow key={t.tarefaAgendadaID} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t.nome}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.5, flexWrap: "wrap" }}>
                      {t.plataforma === "linux" ? (
                        <Chip
                          size="small"
                          label="🐧 Linux"
                          color="info"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "11px", fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="🪟 Windows"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "11px", color: "text.secondary", borderColor: "rgba(0,0,0,0.15)" }}
                        />
                      )}
                      {t.grupoNome && (
                        <Typography variant="caption" color="text.secondary">
                          Grupo: {t.grupoNome}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {renderIconeTipo(t.tipo)}
                      <Typography variant="body2">{t.tipo}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {t.tipoAgendamento} às {t.horaExecucao}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {t.ultimaExecucao ? (
                      <Box>
                        <Typography variant="body2">
                          {formatarDataHoraCurta(t.ultimaExecucao.dataHora)}
                        </Typography>
                        <Chip
                          label={verificarSeSucesso(t.ultimaExecucao.status) ? "Sucesso" : "Falha"}
                          color={verificarSeSucesso(t.ultimaExecucao.status) ? "success" : "error"}
                          size="small"
                          sx={{ height: 16, fontSize: "10px", mt: 0.5 }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Nunca executou
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {t.proximaExecucao ? (
                      <Typography variant="body2">
                        {formatarDataHoraCurta(t.proximaExecucao.dataHora)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Não agendada
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{renderBadgeStatus(t.status)}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={t.status === "ativa"}
                      onChange={(e) => handleAlternarStatus(t, e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Executar Agora (Remoto)">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setTarefaParaExecutar(t)}
                        sx={{
                          mr: 1,
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                          "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.2)" }
                        }}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver Detalhes">
                      <IconButton size="small" onClick={() => onVisualizar(t)} sx={{ mr: 1 }}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => onEditar(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Paginação */}
      {!loading && totalPaginas > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPaginas}
            page={pagina}
            onChange={(_, value) => setPagina(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Modal de Confirmação de Execução Remota */}
      <Dialog
        open={Boolean(tarefaParaExecutar)}
        onClose={() => !executandoRemoto && setTarefaParaExecutar(null)}
        PaperProps={{ sx: { borderRadius: "12px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <PlayArrowIcon color="primary" />
          Executar Tarefa Agora
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Deseja solicitar a <strong>execução manual imediata</strong> da tarefa{" "}
            <strong>"{tarefaParaExecutar?.nome}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            A ordem será enviada para o agente Backup Fácil instalado no servidor do cliente, executando todas as rotinas de backup exatamente como no modo manual local.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setTarefaParaExecutar(null)}
            disabled={executandoRemoto}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarExecucao}
            disabled={executandoRemoto}
            variant="contained"
            color="primary"
            startIcon={executandoRemoto ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
            sx={{ borderRadius: "8px" }}
          >
            {executandoRemoto ? "Enviando comando..." : "Confirmar e Executar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de Sucesso */}
      <Snackbar
        open={Boolean(mensagemSucesso)}
        autoHideDuration={6000}
        onClose={() => setMensagemSucesso(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setMensagemSucesso(null)} severity="success" sx={{ width: "100%", borderRadius: "8px" }}>
          {mensagemSucesso}
        </Alert>
      </Snackbar>

      {/* Modal de Instruções de Instalação Linux */}
      <ModalInstrucoesLinux
        open={modalLinuxAberto}
        onClose={() => setModalLinuxAberto(false)}
        clienteNome={
          (login.usuario?.usuarioCliente as any)?.Cliente?.nomeFantasia ||
          (login.usuario?.usuarioCliente as any)?.cliente?.nomeFantasia ||
          (login.usuario?.usuarioCliente as any)?.Cliente?.razaoSocial ||
          (login.usuario?.usuarioCliente as any)?.cliente?.razaoSocial ||
          "Cliente"
        }
        clienteID={String(cliente.id || login.usuario?.usuarioCliente?.clienteID || "")}
        token={login.accessToken}
        isPosCadastro={false}
      />
    </Box>
  );
};
